import { useState } from 'react'
import { Asset, AssetQuery, DateRange } from '../types'
import { isAfter, isBefore } from 'date-fns'
import { useSelector } from 'react-redux'
import { RootState } from '../redux'

export type AssetStatusOption = 'online' | 'offline' | 'virtual' | 'detached'

export interface UseAssetStatusOptions {
  query: AssetQuery
  onQueryChange?: (query: AssetQuery) => void
  selectedStatusOptions?: AssetStatusOption[]
  onStatusSelectionChange?: (values: AssetStatusOption[]) => void
}

export function getSelectedAssetStatusOptions(query: AssetQuery): AssetStatusOption[] {
  if (query.status === true) {
    const selectedOptions: AssetStatusOption[] = ['online']

    if (query.virtual === true) {
      selectedOptions.push('virtual')
    }

    if (query.withDetached === true) {
      selectedOptions.push('detached')
    }

    return selectedOptions
  }

  if (query.status === false) {
    const selectedOptions: AssetStatusOption[] = ['offline']

    if (query.virtual === true) {
      selectedOptions.push('virtual')
    }

    if (query.withDetached === true) {
      selectedOptions.push('detached')
    }

    return selectedOptions
  }

  if (query.virtual === true) {
    const selectedOptions: AssetStatusOption[] = ['virtual']

    if (query.withDetached === true) {
      selectedOptions.push('detached')
    }

    return selectedOptions
  }

  if (query.virtual === false) {
    const selectedOptions: AssetStatusOption[] = ['online', 'offline']

    if (query.withDetached === true) {
      selectedOptions.push('detached')
    }

    return selectedOptions
  }

  if (query.withDetached === true) {
    const selectedOptions: AssetStatusOption[] = ['detached']

    if (!query.detached) {
      selectedOptions.push('online', 'offline', 'virtual')
    }

    return selectedOptions
  }

  return []
}

export function useAssetStatusOptions({
  query,
  onQueryChange,
  selectedStatusOptions: controlledSelectedStatusOptions,
  onStatusSelectionChange
}: UseAssetStatusOptions) {
  const [internalSelectedStatusOptions, setInternalSelectedStatusOptions] = useState<
    AssetStatusOption[]
  >(() => getSelectedAssetStatusOptions(query))

  const hasStatusQuery =
    query.status !== undefined ||
    query.virtual !== undefined ||
    query.detached !== undefined ||
    query.withDetached === true

  const selectedStatusOptions =
    controlledSelectedStatusOptions ??
    (hasStatusQuery ? getSelectedAssetStatusOptions(query) : internalSelectedStatusOptions)

  const options: AssetStatusOption[] = ['online', 'offline', 'virtual', 'detached']

  function onSelectedStatusOptionsChange(values: AssetStatusOption[]): void {
    if (controlledSelectedStatusOptions === undefined) {
      setInternalSelectedStatusOptions([...values])
    }

    onStatusSelectionChange?.(values)

    const withOnline = values.includes('online')
    const withOffline = values.includes('offline')
    const withVirtual = values.includes('virtual')
    const withDetached = values.includes('detached')

    if (withOnline && withOffline && withVirtual) {
      onQueryChange?.({
        ...query,
        status: undefined,
        virtual: undefined,
        detached: undefined,
        withDetached
      })
    } else if (withOnline && withOffline) {
      onQueryChange?.({
        ...query,
        status: undefined,
        virtual: false,
        detached: undefined,
        withDetached
      })
    } else if (withOnline && withVirtual) {
      onQueryChange?.({
        ...query,
        status: true,
        virtual: true,
        detached: undefined,
        withDetached
      })
    } else if (withOffline && withVirtual) {
      onQueryChange?.({
        ...query,
        status: false,
        virtual: true,
        detached: undefined,
        withDetached
      })
    } else if (withOnline) {
      onQueryChange?.({
        ...query,
        status: true,
        virtual: false,
        detached: undefined,
        withDetached
      })
    } else if (withOffline) {
      onQueryChange?.({
        ...query,
        status: false,
        virtual: false,
        detached: undefined,
        withDetached
      })
    } else if (withVirtual) {
      onQueryChange?.({
        ...query,
        status: undefined,
        virtual: true,
        detached: undefined,
        withDetached
      })
    } else if (withDetached) {
      onQueryChange?.({
        ...query,
        status: undefined,
        virtual: undefined,
        withDetached: true,
        detached: true
      })
    } else {
      onQueryChange?.({
        ...query,
        status: undefined,
        virtual: undefined,
        detached: undefined,
        withDetached: false
      })
    }
  }

  return {
    options,
    selectedStatusOptions,
    onSelectedStatusOptionsChange
  }
}

export function useAssetDateRange(asset?: Asset | null): DateRange {
  const { from, to } = useSelector((state: RootState) => state.query)

  const attachedAt =
    asset !== undefined && asset !== null
      ? asset.attachedAt !== undefined && asset.attachedAt !== null
        ? new Date(asset.attachedAt)
        : new Date(asset.createdAt)
      : from

  const detachedAt =
    asset !== undefined && asset !== null
      ? asset?.detachedAt !== undefined && asset.detachedAt !== null
        ? new Date(asset.detachedAt)
        : to
      : to

  return {
    from: isBefore(from, attachedAt) ? attachedAt : from,
    to: isAfter(to, detachedAt) ? detachedAt : to
  }
}
